/**
 * Win / level-complete overlay.
 *
 * Same flat-color visual language as GameOverOverlay: sky-blue panel,
 * pink-star title, navy stats card, navy total-score card. Tap anywhere on
 * the dimmed background (after a brief delay) to continue.
 *
 * Confetti VFX is fired separately by each scene via `partyMode()` /
 * `celebrate()` in `src/ui/Confetti.js`.
 *
 *   overlay.show(w, h, {
 *     title: 'GYŐZTÉL!',
 *     lines: ['Idő: 0:18', 'Átszelt sorok: 48', ...],
 *     score: 1330,
 *     scoreLabel: 'ÖSSZPONTSZÁM',
 *     onContinue: () => showScoreSubmit(),
 *   });
 */
import { Container } from '../../vendor/pixi.min.mjs';
import { t } from '../config/i18n.js';
import { injectOverlayVars } from './overlayPalette.js';

let _cssInjected = false;
function injectCss() {
    if (_cssInjected) return;
    _cssInjected = true;
    injectOverlayVars();
    const css = `
    @keyframes lc-tap-pulse {
        0%, 100% { opacity: 0.55; }
        50%      { opacity: 1.0; }
    }
    .lc-root { position:fixed; inset:0; z-index:9100; display:flex; align-items:center; justify-content:center;
      background:var(--ov-backdrop); font-family:'Poppins','Inter','Segoe UI',Arial,sans-serif;
      cursor:pointer; overflow:hidden; }
    .lc-panel { position:relative; background:var(--ov-panel-bg);
      border:var(--ov-panel-border-width) solid var(--ov-panel-border);
      /* Sharp corners — modal frame is square per UX direction. */
      border-radius:0; padding:34px 46px 28px;
      min-width:520px; max-width:760px; text-align:center;
      box-shadow:0 24px 60px rgba(0,0,0,0.45); }
    .lc-trophy { font-size:84px; line-height:1; margin-bottom:4px;
      filter: drop-shadow(0 4px 0 rgba(0,0,0,0.25)); }
    .lc-title-row { display:flex; align-items:center; justify-content:center; gap:22px; margin-bottom:22px; }
    .lc-star { color:var(--ov-star); font-size:38px; line-height:1;
      filter:drop-shadow(0 2px 0 rgba(0,0,0,0.18)); }
    .lc-title { font-size:48px; font-weight:900; color:var(--ov-title); letter-spacing:1.5px;
      text-shadow:0 3px 0 var(--ov-title-shadow); }
    .lc-card { background:var(--ov-card-bg);
      border-radius:0; padding:18px 28px; margin:0 0 18px; }
    .lc-stats-line { color:var(--ov-value); font-size:22px; font-weight:700;
      line-height:1.45; }
    .lc-score-label { color:var(--ov-label); font-size:18px; font-weight:700;
      letter-spacing:1.6px; margin-bottom:6px; }
    .lc-score-value { color:var(--ov-accent-score); font-size:56px; font-weight:900;
      text-shadow:0 3px 0 var(--ov-value-shadow); }
    .lc-tap-pill { display:inline-block; background:var(--ov-subtitle-bg);
      color:var(--ov-subtitle-fg);
      font-size:19px; font-weight:800; letter-spacing:0.5px;
      padding:10px 26px; border-radius:0; opacity:0;
      animation: lc-tap-pulse 1.8s ease-in-out infinite; animation-delay:0.6s; }
    `;
    const style = document.createElement('style');
    style.dataset.lc = '1';
    style.textContent = css;
    document.head.appendChild(style);
}

export class LevelCompleteOverlay {
    container = new Container();
    /** @type {HTMLDivElement|null} */
    _root = null;
    _tapEl = null;
    _tapCallback = null;
    _tapReady = false;
    _tapTimer = 0;

    /**
     * @param {number} _screenW
     * @param {number} _screenH
     * @param {{
     *   title?: string,
     *   lines?: string[],
     *   score?: number|string,
     *   scoreLabel?: string,
     *   onContinue: () => void,
     * }} opts
     */
    show(_screenW, _screenH, opts) {
        injectCss();

        const { title, lines = [], score, scoreLabel, onContinue } = opts;
        this._tapCallback = onContinue;
        this._tapReady = false;
        this._tapTimer = 0;

        const root = document.createElement('div');
        root.className = 'lc-root';

        const panel = document.createElement('div');
        panel.className = 'lc-panel';
        root.appendChild(panel);

        const trophy = document.createElement('div');
        trophy.className = 'lc-trophy';
        trophy.textContent = '🏆';
        panel.appendChild(trophy);

        const titleRow = document.createElement('div');
        titleRow.className = 'lc-title-row';
        titleRow.innerHTML = `
            <span class="lc-star">★</span>
            <span class="lc-title">${escapeHtml(title ?? t('common.you_win'))}</span>
            <span class="lc-star">★</span>
        `;
        panel.appendChild(titleRow);

        if (lines.length) {
            const statsBox = document.createElement('div');
            statsBox.className = 'lc-card';
            statsBox.innerHTML = lines.map(line =>
                `<div class="lc-stats-line">${escapeHtml(line)}</div>`
            ).join('');
            panel.appendChild(statsBox);
        }

        if (score !== undefined) {
            const scoreBox = document.createElement('div');
            scoreBox.className = 'lc-card';
            const lbl = document.createElement('div');
            lbl.className = 'lc-score-label';
            lbl.textContent = scoreLabel ?? t('common.total_score');
            scoreBox.appendChild(lbl);
            const val = document.createElement('div');
            val.className = 'lc-score-value';
            val.textContent = String(score);
            scoreBox.appendChild(val);
            panel.appendChild(scoreBox);
        }

        const tapPill = document.createElement('div');
        tapPill.className = 'lc-tap-pill';
        tapPill.textContent = t('common.tap_continue');
        panel.appendChild(tapPill);
        this._tapEl = tapPill;

        root.onclick = () => {
            if (!this._tapReady) return;
            if (this._tapCallback) this._tapCallback();
        };

        document.body.appendChild(root);
        this._root = root;
    }

    update(delta) {
        this._tapTimer += delta / 60;
        if (!this._tapReady && this._tapTimer >= 0.6) {
            this._tapReady = true;
            if (this._tapEl) this._tapEl.style.opacity = '1';
        }
    }

    destroy() {
        if (this._root) {
            this._root.onclick = null;
            this._root.remove();
            this._root = null;
        }
        this._tapEl = null;
        this._tapCallback = null;
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this.container.removeChildren?.();
    }
}

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
